import React from 'react';
import PropTypes from 'prop-types';

import {Dialog, DialogActions, DialogContent, DialogTitle} from 'views/components';
import {Box, Button, Dropdown, DropdownMenuItem, Typography} from 'views/components';
import {MarkdownEditor, Translate, translate} from 'views/module';

import CompactDropdown from './CompactDropdown';

const StageDropdown = (props) => {
  const {selectedStage, setSelectedStage} = props;
  const [isReasonDialogOpen, setIsReasonDialogOpen] = React.useState(false);
  const [pendingStage, setPendingStage] = React.useState(null);
  const [statusReason, setStatusReason] = React.useState('');
  const [presetReason, setPresetReason] = React.useState('');

  const statusRequiringReason = ['INACTIVE'];
  const statusRequiringPresetReason = ['CLOSED_LOST'];
  const variant = props.variant || 'default';
  const isCompact = variant === 'compact';

  const stages = Object.entries(translate('client.Status'));
  const presetReasons = Object.entries(translate('client.ClosedLostReason'));
  const isPresetReason = statusRequiringPresetReason.includes(pendingStage);
  const presetReasonTitle = presetReason ? translate(`client.ClosedLostReason.${presetReason}.Title`) : '';
  const pendingStageTitle = pendingStage ? translate(`client.Status.${pendingStage}.Title`) : '';
  const selectedStageTitle = selectedStage ? translate(`client.Status.${selectedStage}.Title`) : '-';

  const closeReasonDialog = () => {
    setIsReasonDialogOpen(false);
    setPendingStage(null);
    setStatusReason('');
    setPresetReason('');
  };

  const handleOptionSelect = (selectedOption) => {
    if (!selectedOption || selectedOption === selectedStage) {
      return;
    }

    if (statusRequiringReason.includes(selectedOption) || statusRequiringPresetReason.includes(selectedOption)) {
      setPendingStage(selectedOption);
      setIsReasonDialogOpen(true);
      return;
    }

    setSelectedStage(selectedOption);
  };

  const handleSaveReason = () => {
    const reason = isPresetReason ? presetReasonTitle : statusReason.trim();
    if (!pendingStage || !reason) {
      return;
    }

    setSelectedStage(pendingStage, {
      blob: reason,
      editorType: 'MARKDOWN',
    });
    closeReasonDialog();
  };

  return (
    <>
      {
        isCompact &&
        <CompactDropdown
          getOptionKey={([key]) => key}
          getOptionLabel={([, value]) => value.Title}
          isOptionSelected={([key]) => selectedStage === key}
          label={props.label}
          menuLabel={props.label}
          onOptionSelect={([key]) => handleOptionSelect(key)}
          options={stages}
          value={selectedStageTitle}
        />
      }
      {
        !isCompact &&
          <Box>
            <Dropdown
              onChange={handleOptionSelect}
              value={selectedStage}
              label={translate('client.ClientDetail.Status.Menu.Subtitles.Stage')}
            >
              {
                stages.map(([k, v]) => {
                  return (
                    <DropdownMenuItem key={k} value={k}>
                      {v.Title}
                    </DropdownMenuItem>
                  );
                })
              }
            </Dropdown>
          </Box>
      }
      {
        isReasonDialogOpen &&
        <Dialog onClose={closeReasonDialog} open={isReasonDialogOpen}>
          <DialogTitle onClose={closeReasonDialog}>
            <Translate expression='client.ClientDetail.ChangeStatusDialog.ReasonTitle' />
          </DialogTitle>
          <DialogContent>
            <Box sx={{mt: 1, minWidth: {sm: '28rem'}}}>
              <Typography sx={{mb: 2}}>
                <Translate
                  expression='client.ClientDetail.ChangeStatusDialog.ReasonPrompt'
                  substitute={{status: pendingStageTitle}}
                />
              </Typography>
              {
                isPresetReason &&
                <Dropdown
                  onChange={setPresetReason}
                  value={presetReason}
                  label={translate('client.ClientDetail.ChangeStatusDialog.Reason')}
                >
                  {
                    presetReasons.map(([k, v]) => {
                      return (
                        <DropdownMenuItem key={k} value={k}>
                          {v.Title}
                        </DropdownMenuItem>
                      );
                    })
                  }
                </Dropdown>
              }
              {
                !isPresetReason &&
                <>
                  <Typography variant="body2" sx={{mb: 1}}>
                    {translate('client.ClientDetail.ChangeStatusDialog.Reason')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
                    {translate('client.ClientDetail.ChangeStatusDialog.ReasonPlaceholder')}
                  </Typography>
                  <MarkdownEditor
                    autoFocus
                    height={220}
                    value={statusReason}
                    onChange={(value) => setStatusReason(value || '')}
                  />
                </>
              }
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant='outlined' onClick={closeReasonDialog}>
              <Translate expression='generic.Cancel' />
            </Button>
            <Button
              color="primary"
              onClick={handleSaveReason}
              disabled={isPresetReason ? !presetReason : !statusReason.trim()}
            >
              <Translate expression='generic.Save' />
            </Button>
          </DialogActions>
        </Dialog>
      }
    </>
  );
};

StageDropdown.propTypes = {
  label: PropTypes.string,
  selectedStage: PropTypes.string,
  setSelectedStage: PropTypes.func.isRequired,
  variant: PropTypes.oneOf(['default', 'compact']),
};

export default StageDropdown;
